import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import { formatDate, formatMoney } from '@/lib/format';
import { UNIT_TYPES } from '@/types/app';
import { DeveloperDialog } from './DeveloperDialog';
import { ImportUnitsDialog } from './ImportUnitsDialog';
import { ProjectDialog } from './ProjectDialog';
import {
  useDevelopers,
  useProjects,
  useReleaseUnit,
  useUnits,
  type Developer,
  type Project,
  type UnitFilters,
} from './useInventory';

const TABS = ['units', 'projects', 'developers'] as const;
type Tab = (typeof TABS)[number];

const AVAILABILITY = ['available', 'reserved', 'sold', 'blocked'] as const;

const AVAILABILITY_STYLES: Record<string, string> = {
  available: 'bg-green-100 text-green-900',
  reserved: 'bg-amber-100 text-amber-900',
  sold: 'bg-slate-200 text-slate-700',
  blocked: 'bg-red-100 text-red-900',
};

/**
 * Inventory management and the unit browser (#19, #20, #21).
 *
 * Everyone can read inventory — a price list is not confidential inside the company
 * and every rep needs it to do their job. Editing is restricted to sales managers
 * and admins, which mirrors the database exactly: `developers`, `projects` and
 * `units` have no INSERT or UPDATE policy at all, so a hidden button is a courtesy,
 * not the control.
 *
 * The tab lives in the URL so a filtered unit list can be sent to a colleague, and
 * so a refresh does not dump a rep back on the developers tab.
 */
export default function InventoryPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const { data: me } = useProfile();

  const canEdit = !!me && ['sales_manager', 'admin'].includes(me.role);
  const canRelease = canEdit;

  const tabParam = params.get('tab');
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'units';

  const setTab = (next: Tab) => {
    params.set('tab', next);
    setParams(params, { replace: true });
  };

  const [filters, setFilters] = useState<UnitFilters>({});
  const [importing, setImporting] = useState(false);
  const [editingDeveloper, setEditingDeveloper] = useState<Developer | undefined>();
  const [developerOpen, setDeveloperOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | undefined>();
  const [projectOpen, setProjectOpen] = useState(false);

  const developers = useDevelopers();
  const projects = useProjects();
  const units = useUnits(filters);
  const release = useReleaseUnit();

  const setFilter = (patch: Partial<UnitFilters>) => setFilters((f) => ({ ...f, ...patch }));

  const developerName = (id: string) => developers.data?.find((d) => d.id === id)?.name ?? '—';

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">{t('inventory.title')}</h1>

        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setImporting(true)}>
              {t('inventory.importUnits')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setEditingProject(undefined);
                setProjectOpen(true);
              }}
            >
              {t('inventory.addProject')}
            </Button>
            <Button
              onClick={() => {
                setEditingDeveloper(undefined);
                setDeveloperOpen(true);
              }}
            >
              {t('inventory.addDeveloper')}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === name
                ? 'border-brand-700 text-brand-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t(`inventory.${name}` as 'inventory.units')}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------- units */}
      {tab === 'units' ? (
        <>
          <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-5">
            <Select
              label={t('inventory.projects')}
              placeholder={t('inventory.allProjects')}
              value={filters.projectId ?? ''}
              onChange={(e) => setFilter({ projectId: e.target.value })}
              options={(projects.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
            <Select
              label={t('inventory.type')}
              placeholder={t('inventory.allTypes')}
              value={filters.type ?? ''}
              onChange={(e) => setFilter({ type: e.target.value })}
              options={UNIT_TYPES.map((u) => ({
                value: u,
                label: t(`unitTypes.${u}` as 'unitTypes.apartment'),
              }))}
            />
            <Select
              label={t('inventory.availability')}
              placeholder={t('inventory.allAvailability')}
              value={filters.availability ?? ''}
              onChange={(e) => setFilter({ availability: e.target.value })}
              options={AVAILABILITY.map((a) => ({
                value: a,
                label: t(`availability.${a}` as 'availability.available'),
              }))}
            />
            <Input
              label={t('inventory.minPrice')}
              type="number"
              inputMode="numeric"
              dir="ltr"
              value={filters.minPrice ?? ''}
              onChange={(e) => setFilter({ minPrice: e.target.value })}
            />
            <Input
              label={t('inventory.maxPrice')}
              type="number"
              inputMode="numeric"
              dir="ltr"
              value={filters.maxPrice ?? ''}
              onChange={(e) => setFilter({ maxPrice: e.target.value })}
            />
          </div>

          {units.isError ? <Alert tone="error">{t('common.error')}</Alert> : null}
          {release.isError ? (
            <Alert tone="error">
              {release.error instanceof Error ? release.error.message : t('common.error')}
            </Alert>
          ) : null}

          {units.isPending ? (
            <Spinner />
          ) : (units.data ?? []).length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              {t('inventory.noUnits')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.code')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.projects')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.type')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.beds')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.size')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.price')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.down')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('inventory.availability')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(units.data ?? []).map((unit) => (
                    <tr key={unit.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-900">{unit.unit_code}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {unit.projects?.name ?? '—'}
                        {unit.projects?.area ? (
                          <div className="text-xs text-slate-500">{unit.projects.area}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {t(`unitTypes.${unit.type}` as 'unitTypes.apartment')}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">
                        {unit.bedrooms ?? '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-700">
                        {unit.area_sqm ? `${unit.area_sqm} م²` : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-900" dir="ltr">
                        {formatMoney(unit.price_total)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-600" dir="ltr">
                        {formatMoney(unit.down_payment)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            AVAILABILITY_STYLES[unit.availability] ?? 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {t(`availability.${unit.availability}` as 'availability.available')}
                        </span>

                        {unit.availability === 'reserved' && unit.reserved_until ? (
                          <div className="mt-1 text-xs text-slate-500">
                            {t('inventory.reservedUntil', {
                              date: formatDate(unit.reserved_until),
                            })}
                            {canRelease ? (
                              <button
                                type="button"
                                className="ms-2 text-brand-700 hover:underline"
                                onClick={() => release.mutate({ unitId: unit.id })}
                              >
                                {t('inventory.release')}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      {/* ---------------------------------------------------------- projects */}
      {tab === 'projects' ? (
        projects.isPending ? (
          <Spinner />
        ) : (projects.data ?? []).length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            {t('inventory.noProjects')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.name')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.developer')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.area')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.deliveryDate')}
                  </th>
                  <th scope="col" className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(projects.data ?? []).map((project) => (
                  <tr key={project.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{project.name}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {developerName(project.developer_id)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{project.area ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                      {formatDate(project.delivery_date)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {canEdit ? (
                        <button
                          type="button"
                          aria-label={t('inventory.editProject')}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                          onClick={() => {
                            setEditingProject(project);
                            setProjectOpen(true);
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {/* -------------------------------------------------------- developers */}
      {tab === 'developers' ? (
        developers.isPending ? (
          <Spinner />
        ) : (developers.data ?? []).length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            {t('inventory.noDevelopers')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.name')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.areas')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.commission')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('inventory.contactName')}
                  </th>
                  <th scope="col" className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(developers.data ?? []).map((developer) => (
                  <tr key={developer.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{developer.name}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {developer.areas.length > 0 ? developer.areas.join('، ') : '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-700" dir="ltr">
                      {developer.commission_percent ? `${developer.commission_percent}%` : '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {developer.contact_name ?? '—'}
                      {developer.contact_phone ? (
                        <div className="text-xs text-slate-500" dir="ltr">
                          {developer.contact_phone}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {canEdit ? (
                        <button
                          type="button"
                          aria-label={t('inventory.editDeveloper')}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                          onClick={() => {
                            setEditingDeveloper(developer);
                            setDeveloperOpen(true);
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      <DeveloperDialog
        open={developerOpen}
        developer={editingDeveloper}
        onClose={() => setDeveloperOpen(false)}
      />
      <ProjectDialog
        open={projectOpen}
        project={editingProject}
        onClose={() => setProjectOpen(false)}
      />
      <ImportUnitsDialog open={importing} onClose={() => setImporting(false)} />
    </section>
  );
}
